// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, EMPTY, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, map, mergeMap, withLatestFrom } from "rxjs/operators"
import * as Actions from "../actions"
import * as TagsSlice from "../slice"
import * as UserContextActions from "../../user-context/actions"
import * as UserContextSlice from "../../user-context/slice"
import { ListRecentTagsRequest, ListRecentTagsResponse } from "generated/legebill/site/tag/v4/tag_pb"
import { grpc } from "app/rpc/executor"
import { tagServiceClient } from "app/rpc/client"
import { throttleSame } from "../../../lib/rxutils"
import { isGrpcError } from "../../../lib/rpcutils"
import { StatusCode } from "grpc-web"
import { RootState } from "../../../app/store"
import { RECENT_LIST_TAGS_COUNT } from "../../shared/search-dropdown/config"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

type FetchRecentTagsRpc = (request: ListRecentTagsRequest) => Observable<ListRecentTagsResponse>

export const fetchRecentTags = (fetchRecentTagsRpc: FetchRecentTagsRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.fetchRecentTags.match),
      withLatestFrom(state$),
      // filter out if no current site id
      filter(([, state]) => Boolean(state.userContext.currentSiteId)),
      map(([action, state]) => (
        {
          request: new ListRecentTagsRequest()
            .setSiteId(state.userContext.currentSiteId || "")
            .setActivityDate(state.timeline.activeDate)
            .setPageSize(RECENT_LIST_TAGS_COUNT),
          swallowFailure: action.payload?.swallowFailure ?? false
        }
      )),
      // throttle for the same active date of the same site
      throttleSame(2000, ({ request: a }, { request: b }) =>
        a.getSiteId() === b.getSiteId() && a.getActivityDate() === b.getActivityDate()
      ),
      mergeMap(({ request, swallowFailure }) =>
        of(request)
          .pipe(
            mergeMap(fetchRecentTagsRpc),
            map(t => t.toObject()),
            map(TagsSlice.setRecentTags),
            catchError(e => handleError(e, swallowFailure)),
          )),
    )

const fetchRecentTagsRpc = (request: ListRecentTagsRequest): Observable<ListRecentTagsResponse> =>
  grpc(() => tagServiceClient.listRecentTags(request, null))

const handleError = (error: unknown, swallowFailure: boolean | undefined): Observable<Action> => {
  if (isGrpcError(error)) {
    const { code } = error
    if (code === StatusCode.PERMISSION_DENIED) {
      // clear current site id and re-fetch user (includes sites list) and membership details
      return concat(
        of(UserContextSlice.clearCurrentSiteId()),
        of(UserContextActions.fetchUserDetails()),
      )
    }
  }

  return swallowFailure ?
    EMPTY :
    of(showSnackBar({
      message: t({ id: "msg_fetch_recent_tags_failed", message: "Failed to load recent tags. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
      messageType: "ERROR",
    }))
}

export default fetchRecentTags(fetchRecentTagsRpc)
